import { TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import { IColumn, IRow } from "@components/table";
import { IEntity, TEntityKey } from "@odata/BindingContext";
import { IDocumentLabelEntity, ILabelEntity } from "@odata/GeneratedEntityTypes";
import { ActionTypeCode } from "@odata/GeneratedEnums";
import { getDefaultLogActionDetail, logAction } from "@odata/OData.utils";
import { ODataQueryResult } from "@odata/ODataParser";
import ExcelExport, { ExportType } from "@utils/ExcelExport";
import { isNotDefined } from "@utils/general";
import saveAs from "file-saver";

import { REST_API_URL } from "../../constants";
import customFetch from "../../utils/customFetch";
import { prepareDataForExport } from "../../views/table/TableView.utils";

const GET_REFERENCED_LABELS_PATH = `${REST_API_URL}/LabelHierarchies/GetReferencedLabels`;

export const getUsedLabels = async (labelsIds: TEntityKey[]): Promise<ILabelEntity[]> => {
    const result = await customFetch(`${GET_REFERENCED_LABELS_PATH}?${labelsIds.map(id => `labelIds=${id}`).join("&")}`);
    const labels = await result.json() as ODataQueryResult<ILabelEntity[]>;

    return labels?.value ?? [];
};

export function compareLabels(a: IDocumentLabelEntity[], b: IDocumentLabelEntity[]): boolean {
    if (a?.length !== b?.length) {
        return false;
    }
    return a?.every((label) => b.find(bLabel => bLabel.Label?.Id === label.Label?.Id) !== undefined);
}

export const handleLabelHierarchyExportButton = async (storage: TSmartODataTableStorage, exportType: ExportType, filter?: string): Promise<void> => {
    const { tableAPI } = storage;
    const tableState = tableAPI.getState();

    // process rows to fill in the fake name columns
    const values: IEntity[] = [];
    const _processRow = (row: IRow): IEntity => {
        return {
            ...row.customData.entity,
            Id: row.id,
            Children: row.rows?.map(_processRow)
        };
    };
    for (const key in tableState.rows) {
        if (!Object.prototype.hasOwnProperty.call(tableState.rows, key)) {
            continue;
        }
        const row = tableState.rows[key];
        if (isNotDefined(row.customData.parent)) {
            values.push(_processRow(row));
        }
    }

    const { rows, columns: preparedColumns } = await prepareDataForExport({
        rows: values,
        columns: tableState.columns,
        hierarchyColumns: tableState.childColumns,
        context: storage.context, translate: storage.t,
        hierarchy: "Children",
        storage
    });

    const file = await ExcelExport.export({
        columns: preparedColumns as unknown as IColumn[],
        tableName: storage.data.definition.title,
        rows,
        type: exportType
    });

    // CompanyId is automatically added to OData request and thus is not part of 'filter'
    // but we still want Company Id to be part of the log
    const companyFilter = getDefaultLogActionDetail(storage);
    const logDetail = filter ? `${filter} AND ${companyFilter}` : companyFilter;

    logAction({
        actionId: storage.data.bindingContext.getFullPath(),
        actionType: exportType === ExportType.CSV ? ActionTypeCode.CsvExport : ActionTypeCode.ExcelExport,
        detail: logDetail
    });

    saveAs(file);
};